import { useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@core/ui/cn';
import { pressDrag } from '../shared/interaction';
import type { ISODate } from '@core/dates';
import type { Attention, Task } from '../shared/data';
import { LaunchReleaseCard, ReleaseCard, type CardActions, type Person, type ProjectInfo } from './ReleaseCard';
import { isCurrentWeek, monthTint, type MonthGroup, type Release } from './planner';

type Props = {
  months: MonthGroup[];
  today: ISODate;
  projectOf: (t: Task) => ProjectInfo;
  peopleOf: (t: Task) => Person[];
  attention: Map<string, Attention>;
  actions: CardActions;
  onOpen: (r: Release) => void;
  onDrop: (r: Release, monday: ISODate) => void;
};

/** Три месяца по неделям: заголовок месяца со счётчиком, строки недель с карточками до четырёх в ряд. */
export function WeeksView({ months, today, projectOf, peopleOf, attention, actions, onOpen, onDrop }: Props) {
  // карточка тянется в другую неделю: строка под курсором подсвечивается, на отпускании — дата выхода на её понедельник
  const [drag, setDrag] = useState<{ id: string; over: ISODate | null; dx: number; dy: number } | null>(null);
  // карточка едет под курсором и сама попадала бы под elementFromPoint — на время протяжки она не ловит мышь (pointer-events-none)
  const weekAt = (x: number, y: number) => document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-release-week]')?.dataset['releaseWeek'] ?? null;
  const startDrag = (r: Release, e: ReactPointerEvent<HTMLElement>) => pressDrag(e, r.id, {
    onMove: (dx, dy, ev) => setDrag({ id: r.id, over: weekAt(ev.clientX, ev.clientY), dx, dy }),
    onDrop: (ev) => { setDrag(null); const to = weekAt(ev.clientX, ev.clientY); if (to) onDrop(r, to); },
  });
  return (
    <div className="flex flex-col gap-6">
      {months.map((m) => (
        <section key={m.month} data-release-month={m.month} className="rounded-xl border border-gray-300 overflow-hidden bg-white shadow-md">
          <h3 className="px-4 py-2 font-bold text-base flex items-baseline gap-2" style={{ background: monthTint(m.month).bg, color: monthTint(m.month).fg }}>
            {m.label}<span className="text-sm font-normal opacity-70">· {m.count} {m.count === 1 ? 'release' : 'releases'}</span>
          </h3>
          {m.weeks.map(({ week, releases }) => {
            const current = isCurrentWeek(week, today);
            return (
              <div key={week.monday} data-release-week={week.monday} data-current-week={current || undefined} data-drop-target={drag?.over === week.monday ? '' : undefined} className={cn('grid grid-cols-[112px_1fr] border-t border-gray-200', current && 'bg-red-50/40', drag?.over === week.monday && 'bg-blue-100 ring-2 ring-inset ring-accent')}>
                <div className={cn('px-3 py-2 border-l-4', current ? 'border-red-500' : 'border-transparent')}>
                  <div className="font-bold">W{week.number}</div>
                  <div className="text-xs text-gray-600 whitespace-nowrap">{week.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{releases.length === 0 ? 'no releases' : `${releases.length} ${releases.length === 1 ? 'release' : 'releases'}`}</div>
                  {current && <div className="text-xs font-semibold text-red-600 mt-1">This week</div>}
                </div>
                <div className={cn('p-2 grid gap-2 grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 content-start', releases.length === 0 && 'min-h-10')}>
                  {releases.map((r) => (r.kind === 'launch'
                    ? <div key={r.id} className="relative cursor-default"><LaunchReleaseCard release={r} onOpen={onOpen} /></div>
                    : (
                      <div key={r.id} className={cn('relative', drag?.id === r.id && 'opacity-70 z-10 cursor-grabbing pointer-events-none')} style={drag?.id === r.id ? { transform: `translate(${drag.dx}px, ${drag.dy}px)` } : undefined} onPointerDown={(e) => startDrag(r, e)}>
                        <ReleaseCard task={r.task} project={projectOf(r.task)} people={peopleOf(r.task)} attention={attention.get(r.id)} actions={actions} onOpen={() => onOpen(r)} />
                      </div>
                    )))}
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
